import type { Collectible } from '@/config/objects/collectibles';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  FormPageLayout,
  ImageUploadDropzone,
  Input,
  RichTextEditor,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface CollectibleFormData {
  name: string;
  description: string;
  iconUrl: string;
  imageUrl: string;
  defaultStock: number;
}

const DEFAULT_FORM: CollectibleFormData = {
  name: '',
  description: '',
  iconUrl: '',
  imageUrl: '',
  defaultStock: 0,
};

export function CollectibleFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const [formData, setFormData] = useState<CollectibleFormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [formReady, setFormReady] = useState(!isEdit);

  const updateField = useCallback(
    <K extends keyof CollectibleFormData>(
      key: K,
      value: CollectibleFormData[K],
    ) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  const {
    data: response,
    isLoading,
    isError,
  } = useQuery<{ success: boolean; data: Collectible }>({
    queryKey: ['Collectible', itemId],
    queryFn: () => apiClient.get(`/collectibles/${itemId}`),
    enabled: !!itemId,
  });

  useEffect(() => {
    if (!response?.data) return;
    const c = response.data;
    setFormData({
      name: c.name ?? '',
      description: c.description ?? '',
      iconUrl: c.iconUrl ?? '',
      imageUrl: c.imageUrl ?? '',
      defaultStock: c.defaultStock ?? 0,
    });
    setLastSaved(new Date(c.updatedAt));
    setFormReady(true);
  }, [response]);

  useEffect(() => {
    if (isError) {
      toast.error('Collectible not found');
      navigate('/collectibles');
    }
  }, [isError, navigate]);

  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (formData.defaultStock < 0)
      errs.defaultStock = 'Default stock cannot be negative';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const preparePayload = () => ({
    name: formData.name.trim(),
    description: formData.description.trim() || null,
    iconUrl: formData.iconUrl || null,
    imageUrl: formData.imageUrl || null,
    defaultStock: formData.defaultStock,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isEdit) {
        return apiClient.put<{ data: Collectible }>(
          `/collectibles/${itemId}`,
          payload,
        );
      }
      return apiClient.post<{ data: Collectible }>('/collectibles', payload);
    },
    onSuccess: (resp: { data: Collectible }) => {
      queryClient.invalidateQueries({ queryKey: ['Collectible'] });
      setLastSaved(new Date());
      if (isEdit) {
        toast.success('Collectible saved');
        navigate(`/collectibles/${itemId}`);
      } else {
        toast.success('Collectible created');
        navigate(`/collectibles/${resp.data.id}`);
      }
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to save collectible');
    },
  });

  const handleSave = () => {
    if (!validateForm()) return;
    saveMutation.mutate(preparePayload());
  };

  const handleCancel = () => {
    navigate(isEdit ? `/collectibles/${itemId}` : '/collectibles');
  };

  if (isEdit && (isLoading || !formReady)) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          <Card className="rounded-xl border bg-card shadow-sm">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <Skeleton className="h-5 w-40" />
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout>
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {isEdit ? 'Edit Collectible' : 'Create Collectible'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEdit
                ? formData.name || 'Update collectible details'
                : 'Add a new collectible to the library'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saveMutation.isPending}
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Header>

      <FormPageLayout.Content>
        <div className="space-y-6">
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Collectible Details</CardTitle>
              <CardDescription>Basic information</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormFieldGroup
                name="name"
                label="Name"
                required
                error={errors.name}
              >
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g. Lobster"
                  className={cn(errors.name && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup name="description" label="Description">
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => updateField('description', value ?? '')}
                />
              </FormFieldGroup>

              <FormFieldGroup
                name="defaultStock"
                label="Default Stock"
                description="0 = unlimited. Display-only in v1; not enforced as a hard cap."
                error={errors.defaultStock}
              >
                <Input
                  id="defaultStock"
                  type="number"
                  min={0}
                  value={formData.defaultStock}
                  onChange={(e) =>
                    updateField('defaultStock', parseInt(e.target.value) || 0)
                  }
                />
              </FormFieldGroup>
            </CardContent>
          </Card>

          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Media</CardTitle>
              <CardDescription>Icon (badge) and image (modal)</CardDescription>
            </CardHeader>
            <CardContent className="p-6 grid gap-6 md:grid-cols-2">
              <ImageUploadDropzone
                targetAspectRatio={1}
                label="Icon"
                value={formData.iconUrl || undefined}
                onChange={(mediaId) => updateField('iconUrl', mediaId || '')}
              />
              <ImageUploadDropzone
                targetAspectRatio={4 / 3}
                label="Image"
                value={formData.imageUrl || undefined}
                onChange={(mediaId) => updateField('imageUrl', mediaId || '')}
              />
            </CardContent>
          </Card>
        </div>
      </FormPageLayout.Content>

      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saveMutation.isPending}
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
